import React from 'react';
import { TileDropItem, TileGroupDropItem, Orientation } from '../types/';
import { uniq, find, reject } from 'lodash';
import { dndTypes } from '../helpers/constants';
import { addCommand, addBoard } from '../helpers/gameServer';
import {
    moveTilesOnBoard,
    addTilesToBoardFromHand,
    addTilesToHandFromBoard,
} from '../helpers/gameReducer';
import { BoardState } from '../containers/BoardState';
import { SelectedTilesContainer } from '../containers/SelectedTilesContainer';
import { SelectedBoardTiles } from '../containers/SelectedBoardTiles';

export const useGameCallbacks = ({ userId, roomId }: { userId: string, roomId: string }) => {
    const { state, dispatch } = BoardState.useContainer();
    const { setSelectedTiles } = SelectedTilesContainer.useContainer();
    const { selectedBoardTiles, setSelectedBoardTiles } = SelectedBoardTiles.useContainer();
    const { hand, board } = state;

    const handlePeel = React.useCallback(() => {
        addCommand(roomId, {
            action: 'peel',
            userId,
            seed: new Date().getTime(),
        });
    }, [userId, roomId]);

    const handleDump = React.useCallback(
        (id: number) => {
            addCommand(roomId, {
                action: 'dump',
                userId,
                seed: new Date().getTime(),
                tileToDump: id,
            });
        },
        [userId, roomId],
    );

    const handleDropOnHand = React.useCallback(
        (item: TileDropItem) => {
            dispatch(addTilesToHandFromBoard(uniq([item.id, ...selectedBoardTiles])));
            setSelectedBoardTiles([]);
        },
        [selectedBoardTiles, dispatch, setSelectedBoardTiles],
    );

    const handleDropOnBoard = React.useCallback(
        ({
            item,
            row,
            column,
        }: {
            item: TileDropItem | TileGroupDropItem,
            row: number,
            column: number,
        }) => {
            if (item.type === dndTypes.TILE_GROUP) {
                dispatch(addTilesToBoardFromHand(item.tiles, row, column, item.orientation));
                setSelectedTiles([]);
            } else {
                // If its a tile from the hand
                // remove it from the hand
                // and then set it on the board
                if (find(hand, { id: item.id })) {
                    dispatch(
                        addTilesToBoardFromHand([item.id], row, column, Orientation.HORIZONTAL),
                    );
                } else {
                    const tileOnBoard = find(board, { id: item.id });
                    if (tileOnBoard) {
                        const deltaColumn = column - tileOnBoard.column;
                        const deltaRow = row - tileOnBoard.row;
                        // Else, move it from its other
                        // location on the board
                        dispatch(
                            moveTilesOnBoard({
                                ids: uniq([item.id, ...selectedBoardTiles]),
                                deltaColumn,
                                deltaRow,
                            }),
                        );
                    }
                    setSelectedBoardTiles([]);
                }
            }
        },
        [hand, board, selectedBoardTiles],
    );

    const handleClickTile = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement>, id: number) => {
            setSelectedTiles((tiles) => {
                // If it already exists in the list, deselect
                if (!!find(tiles, (tile) => tile === id)) {
                    return reject(tiles, (tile) => tile === id);
                } else {
                    return [...tiles, id];
                }
            });
        },
        [setSelectedTiles],
    );

    const handleClickSelectedTile = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement>, id: number) => {
            handleClickTile(e, id);
        },
        [handleClickTile],
    );

    const handleClickBoardTile = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement>, id: number) => {
            setSelectedBoardTiles((tiles) => {
                // If it already exists in the list, deselect
                if (!!find(tiles, (tile) => tile === id)) {
                    return reject(tiles, (tile) => tile === id);
                } else {
                    return [...tiles, id];
                }
            });
        },
        [setSelectedBoardTiles],
    );

    const handleDoubleClickBoardTile = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement>, id: number) => {
            dispatch(addTilesToHandFromBoard(uniq([id])));
        },
        [dispatch],
    );

    const handleSubmit = React.useCallback(() => {
        addBoard({ roomId, userId, board });
    }, [board, roomId, userId]);

    return {
        handlePeel,
        handleDump,
        handleDropOnHand,
        handleDropOnBoard,
        handleClickTile,
        handleClickSelectedTile,
        handleClickBoardTile,
        handleDoubleClickBoardTile,
        handleSubmit,
    };
};

